/**
 * What the catalog holds for the Environments this device saved, whichever way it reaches them.
 *
 * SSH destinations and machines paired by link are saved the same way — per device, in Client
 * Settings — and published the same way, so both go through here rather than each keeping a copy.
 */
import type { HostEnvironmentRegistration } from "@afar-research/client-runtime/connection";
import type { PlatformConnectionChange } from "@afar-research/client-runtime/platform";
import type { EnvironmentId } from "@afar-research/contracts";

import {
  getClientSettingsSnapshot,
  persistClientSettingsUpdate,
  subscribeClientSettings,
} from "../clientSettingsStore";

/**
 * Whether the user left this machine switched on. Remembered per device, so a restart restores
 * the switch rather than quietly reconnecting a machine they turned off.
 */
export function isEnabledOnThisDevice(environmentId: EnvironmentId): boolean {
  return !getClientSettingsSnapshot().disabledEnvironmentIds.includes(environmentId);
}

/**
 * What the catalog should hold for one saved set, given what it already holds.
 *
 * The whole saved set is reconciled rather than the one entry that changed: a catalog derived from
 * the full list cannot drift out of step with what was saved, and the list is a handful of entries
 * a person added. Installs rather than replaces, because a replace tears down and recreates an
 * entry's scope even when nothing about it changed, which would drop a live connection every time
 * another machine was added.
 */
export function savedConnectionChanges(input: {
  readonly registrations: ReadonlyArray<HostEnvironmentRegistration>;
  readonly published: ReadonlyArray<EnvironmentId>;
}): {
  readonly changes: ReadonlyArray<PlatformConnectionChange>;
  readonly published: ReadonlyArray<EnvironmentId>;
} {
  const saved = input.registrations.map((registration) => registration.target.environmentId);
  const changes: PlatformConnectionChange[] = [];
  for (const environmentId of input.published) {
    if (!saved.includes(environmentId)) changes.push({ _tag: "RemoveHost", environmentId });
  }
  for (const registration of input.registrations) {
    changes.push({ _tag: "InstallHost", registration });
  }
  return { changes, published: saved };
}

/**
 * Calls `listener` whenever `read()` may have changed, including when Client Settings first
 * arrive from Desktop.
 *
 * The snapshot starts as the defaults and is hydrated asynchronously, so a reader that looked once
 * and then waited for a save would hold an empty list for the whole session: the machines would
 * be saved, and never listed or connected. Following Client Settings itself is what closes that.
 */
export function subscribeSavedSetting<A>(read: () => A, listener: () => void): () => void {
  let last = read();
  const unsubscribe = subscribeClientSettings(() => {
    const next = read();
    if (next === last) return;
    last = next;
    listener();
  });
  // Hydration can land between the caller's own read and this subscription.
  listener();
  return unsubscribe;
}

/**
 * Remembers a machine's switch on this device, so a restart restores it. Stored as the exceptions,
 * so a machine switched back on leaves no record behind.
 */
export async function rememberEnabledOnThisDevice(
  environmentId: EnvironmentId,
  enabled: boolean,
): Promise<void> {
  await persistClientSettingsUpdate((settings) => {
    const others = settings.disabledEnvironmentIds.filter((id) => id !== environmentId);
    return { ...settings, disabledEnvironmentIds: enabled ? others : [...others, environmentId] };
  });
}

/**
 * Drops what this device decided about a machine it forgot. Leaving a GitHub sharing grant behind
 * would let it come back into force if the same machine were added again, which is not something
 * the user asked for when they removed it.
 */
export async function forgetEnvironmentPreferences(environmentId: EnvironmentId): Promise<void> {
  await persistClientSettingsUpdate((settings) => {
    const { [environmentId]: _removed, ...loadBalancingPreferences } =
      settings.loadBalancingPreferences;
    return {
      ...settings,
      loadBalancingPreferences,
      gitHubRoutingPermissions: settings.gitHubRoutingPermissions.filter(
        (entry) => entry.environmentId !== environmentId,
      ),
      disabledEnvironmentIds: settings.disabledEnvironmentIds.filter((id) => id !== environmentId),
    };
  });
}
